import _ from "lodash"
import { createTheme } from "@material-ui/core/styles"
import { red, amber, green, blue, grey } from "@material-ui/core/colors"
import {
  appTheme as appThemeDef,
  tableTheme as tableThemeDef,
  widgetTableTheme as widgetTableThemeDef
} from "./themes"

// appThemeDef and tableThemeDef are style overriding that come from plugin
// We must merge them with default theming to customize the application how we want

const defaultAppThemeDesc = {
  palette: {
    common: {
      white: "#f1f1f1",
      whiteCream: "#fcfcfc",
      lightGray: "#eaebf2",
      lightRose: red[500],
      lightGreen: green[500],
      gray: "#828282",
      black: "#1c1c1c"
    },
    primary: {
      light: "#3f4752",
      main: "#373e47",
      mainVariant1: grey[300],
      mainVariant2: grey[400],
      mainVariant3: grey[500],
      dark: "#1c313a",
      contrastText: "#ffcc80"
    },
    secondary: {
      light: "#fffbd1",
      main: "#d3a653",
      dark: "#a99771",
      contrastText: "#2b2b2b"
    },
    text: {
      primary: "#2b2b2b",
      secondary: "#a5a5a5",
      contrastText: "#373e47"
    },
    error: {
      light: red[50],
      main: red[400],
      dark: "#74272c"
    },
    warning: {
      light: amber[100],
      main: amber[400]
    },
    // ** W A R N I N G **
    // success & info are used by @material-ui/lab/Alert
    success: {
      main: green[200],
      dark: green[500]
    },
    info: {
      main: blue[200]
    }
  },
  typography: {
    // Use the system font instead of the default Roboto font.
    fontFamily: ['"Lato"', "sans-serif"].join(",")
  },
  shape: {
    borderRadius: 2
  }
}

export const appThemeDesc = _.merge(defaultAppThemeDesc, appThemeDef)

export const defaultAppTheme = {
  overrides: {
    // Header
    MuiAppBar: {
      root: {
        borderBottom: "none",
        fontSize: "12px",
        // Layout
        "& .MuiToolbar-root": {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        },
        // Tabs
        "& .MuiTabs-indicator": {
          display: "none"
        },
        // Logo
        "& .header-logo-wrapper": {
          display: "flex",
          alignItems: "center"
        },
        "& .header-logo": {
          width: "100px",
          marginRight: "40px",
          alignSelf: "center"
        },
        // User menu
        "& .header-admin-chip": {
          backgroundColor: appThemeDesc.palette.error.main,
          color: appThemeDesc.palette.common.lightGray,
          fontWeight: "bold",
          marginRight: "12px"
        },
        "& .header-admin-chip:hover": {
          backgroundColor: red[300]
        },
        "& .header-admin-chip .MuiChip-avatar": {
          color: red[900]
        },
        "& .header-user-profile": {
          backgroundColor: appThemeDesc.palette.primary.light,
          borderRadius: "2px",
          cursor: "pointer",
          padding: "4px 10px"
        },
        "& .header-user-profile:hover": {
          filter: "brightness(120%)"
        },
        "& .header-user-profile .MuiTypography-root": {
          fontSize: "14px",
          paddingLeft: "20px"
        }
      }
    },

    // Text
    // Tabs
    MuiTab: {
      root: {
        letterSpacing: "1px",
        margin: "0 5px",
        "@media (min-width: 100px)": {
          minWidth: "auto",
          fontSize: "12px"
        },
        "&.Mui-selected": {
          fontWeight: "600"
        },

        "&.inner-tab": {
          background: appThemeDesc.palette.common.lightGray,
          borderRadius: "2px",
          fontSize: "12px",
          fontWeight: "normal",
          color: appThemeDesc.palette.primary.main,
          outline: "none",
          boxShadow: "none",
          margin: "0 2px",
          border: "0",
          cursor: "pointer",
          padding: "5px 10px",
          minHeight: "auto"
        },
        "&.inner-tab:hover": {
          color: appThemeDesc.palette.primary.light
        },
        "&.inner-tab.Mui-selected": {
          background: appThemeDesc.palette.common.tabBgSelected,
          color: appThemeDesc.palette.common.white,
          cursor: "default",
          transition: "background 0.8s ease"
        }
      }
    },
    MuiTabs: {
      flexContainer: {
        justifyContent: "center"
      },
      root: {
        "&.inner-tabs": {
          marginTop: "20px"
        },
        "&.inner-tabs .MuiTabs-indicator": {
          display: "none"
        }
      }
    },

    // Cards
    MuiPaper: {
      root: {
        width: "auto"
      }
    },
    MuiCard: {
      root: {
        padding: "0",
        "&.card-primary": {
          backgroundColor: appThemeDesc.palette.primary.main,
          border: `1px solid${appThemeDesc.palette.primary.main}`
        },
        "&.card-secondary": {
          backgroundColor: appThemeDesc.palette.secondary.main,
          border: `1px solid${appThemeDesc.palette.secondary.main}`
        },
        "&.data-widget-card": {
          borderRadius: "2px",
          width: "100%",
          height: "100%",

          "& .MuiCardContent-root": {
            position: "relative",
            padding: "30px 30px 50px"
          },

          "& h3": {
            color: appThemeDesc.palette.primary.light
          },

          "& .tag-label": {
            background: appThemeDesc.palette.common.lightGray,
            padding: "0 4px",
            borderRadius: "2px",
            textTransform: "uppercase",
            marginBottom: "10px",
            marginLeft: "10px",
            display: "flex",
            alignItems: "center",

            "& svg": {
              color: appThemeDesc.palette.primary.light,
              fontSize: "15px"
            }
          },

          "& .filter-btn": {
            position: "absolute",
            bottom: "0",
            right: "0",
            margin: "5px",
            borderRadius: "2px"
          },

          "& .data-widget-subtitle": {
            borderTop: `1px solid ${appThemeDesc.palette.common.lightGray}`,
            textTransform: "uppercase",
            fontSize: "11px",
            marginTop: "5px",
            paddingTop: "5px",
            fontWeight: "bold"
          }
        }
      }
    },
    MuiCardHeader: {
      root: {
        backgroundColor: appThemeDesc.palette.common.cardHeaderBg,
        "& h1": {
          fontSize: "14px",
          textTransform: "uppercase",
          fontWeight: "600",
          letterSpacing: "1px",
          color: appThemeDesc.palette.secondary.main
        },
        "& .MuiCardHeader-title": {
          fontSize: "20px",
          fontWeight: "bold",
          color: appThemeDesc.palette.common.whiteCream,
          textTransform: "none"
        },
        "& .MuiButton-label": {
          color: appThemeDesc.palette.secondary.main,
          fontSize: "12px",
          textTransform: "uppercase",
          fontWeight: "600",
          letterSpacing: "1px"
        }
      },
      action: {
        marginTop: "0",
        marginRight: "0",
        "& .MuiButton-text": {
          padding: "0",
          margin: "0"
        }
      }
    },
    MuiCardActions: {
      root: {
        display: "flex",
        justifyContent: "center",
        padding: "15px 0"
      }
    },

    // Forms
    MuiFormGroup: {
      root: {
        maxWidth: "500px",
        width: "100%",
        margin: "auto"
      }
    },
    MuiFormControl: {
      root: {
        maxWidth: "500px",
        margin: "10px 0px"
      }
    },
    MuiInputBase: {
      root: {
        // maxWidth: "500px",
        background: appThemeDesc.palette.common.whiteCream
      }
    },
    MuiFormControlLabel: {
      root: {
        maxWidth: "500px",
        width: "auto",
        margin: "5px 0"
      }
    },
    MuiFormLabel: {
      root: {
        color: appThemeDesc.palette.common.gray
      }
    },
    MuiAutocomplete: {
      root: {
        width: "100%",
        maxWidth: "500px"
      }
    },
    MuiSelect: {
      root: {
        borderColor: `${appThemeDesc.palette.common.lightGray} !important`,
        background: `${appThemeDesc.palette.common.whiteCream} !important`
      },
      selectMenu: {
        paddingRight: "30px !important"
      }
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: appThemeDesc.palette.common.lightGray
      }
    },

    // Dialog
    MuiDialog: {
      root: {
        "& .MuiPaper-root": {
          "& .MuiCheckbox-root": {
            position: "relative",
            top: "auto",
            right: "auto",
            padding: "auto",
            color: appThemeDesc.palette.secondary.main
          }
        }
      },
      paper: {
        minWidth: "400px"
      }
    },

    MuiContainer: {
      root: {
        // TREE
        "& .dashboard-widget-layout .dashboard-widget-layout-tabrow": {
          marginLeft: "-2px",
          marginRight: "-2px"
        },
        "& .dashboard-widget-layout .dashboard-widget-layout-tabrow .dashboard-widget-layout-tab":
          {
            background: appThemeDesc.palette.common.lightGray,
            borderRadius: "2px",
            fontSize: "12px",
            fontWeight: "normal",
            padding: "4px",
            color: appThemeDesc.palette.primary.main,
            outline: "none",
            boxShadow: "none",
            margin: "0 2px 10px",
            border: "0",
            cursor: "pointer"
          },
        "& .dashboard-widget-layout .dashboard-widget-layout-tabrow .dashboard-widget-layout-tab:hover":
          {
            color: appThemeDesc.palette.primary.light
          },
        "& .dashboard-widget-layout .dashboard-widget-layout-tabrow .dashboard-widget-layout-tab.dashboard-widget-layout-tab-selected":
          {
            background: appThemeDesc.palette.common.tabBgSelected,
            color: appThemeDesc.palette.common.white,
            cursor: "default",
            transition: "background 0.8s ease"
          },
        "& .tree-widget table": {
          borderSpacing: "0",
          margin: "auto",
          width: "100%",
          boxShadow:
            "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)"
        },
        "& .tree-widget table thead tr th": {
          color: appThemeDesc.palette.tableHead.color,
          textAlign: "left",
          backgroundColor: appThemeDesc.palette.tableHead.backgroundColor,
          fontSize: "12px",
          fontWeight: "bold",
          padding: "4px 8px",
          borderLeft: `1px solid ${appThemeDesc.palette.secondary.main}`
        },
        "& .tree-widget table thead tr th:first-child": {
          borderLeft: "0px"
        },
        "& .tree-widget table > tbody": {
          maxHeight: "200px",
          overflow: "auto"
        },
        "& .tree-widget table td": {
          fontSize: "12px",
          padding: "4px 8px",
          borderLeft: `1px solid ${appThemeDesc.palette.table.border}`,
          borderBottom: `1px solid ${appThemeDesc.palette.common.lightGray}`
        },
        "& .tree-widget table tr td:first-child": {
          borderLeft: "0px"
        },
        // "& .tree-widget table tr:hover td": {
        //   backgroundColor: "#EDEDED"
        // },
        "& .tree-widget table .tree-fold-button": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0",
          width: "15px",
          height: "15px",
          background: appThemeDesc.palette.primary.main,
          border: "none",
          outline: "none",
          marginRight: "5px"
        },
        "& .tree-widget table .tree-fold-button-folded:after": {
          content: '"+"',
          fontSize: "12px",
          fontWeight: "bold",
          color: appThemeDesc.palette.common.white
        },
        "& .tree-widget table .tree-fold-button-expanded:after": {
          content: '"-"',
          fontSize: "12px",
          fontWeight: "bold",
          color: appThemeDesc.palette.common.white,
          marginTop: "-2px"
        },
        "& .tree-widget table .tree-padding": {
          paddingLeft: "18px"
        },
        "& .tree-widget table .tree-fold-button + span": {
          color: appThemeDesc.palette.primary.main,
          fontWeight: "bold",
          background: appThemeDesc.palette.common.lightGray,
          padding: "0 4px"
        },

        // Highcharts container
        "& .highcharts-container-style": {
          marginTop: "8px",
          marginBottom: "8px"
        },

        // Widget separator
        "& .dashboard-widget-layout-vertical-separator": {
          margin: "0px 8px"
        },

        "& .dashboard-widget-layout-horizontal-separator": {
          margin: "8px 0px"
        },

        // Admin login custom page

        // Skeleton
        "& .skeleton-title": {
          width: "100%"
        },

        "& .widget-data": {
          border: `1px solid ${appThemeDesc.palette.common.whiteCream}`,
          padding: "5px 10px",
          borderRadius: "2px",
          overflow: "hidden"
        },

        "& .tag-label": {
          background: appThemeDesc.palette.common.whiteCream,
          color: appThemeDesc.palette.secondary.dark,
          padding: "2px 4px",
          borderRadius: "2px",
          marginRight: "7px",
          textTransform: "uppercase",
          fontSize: "11px",
          letterSpacing: "1px",
          fontWeight: "bold",
          lineHeight: "25px"
        },

        "& .skeleton-warning": {
          background: appThemeDesc.palette.error.light,

          "& .MuiTypography-root": {
            color: appThemeDesc.palette.error.dark
          },

          "& .skeleton-icon": {
            padding: "12px"
          },

          "& svg": {
            color: appThemeDesc.palette.error.main
          }
        },

        "& .page-subtitle": {
          margin: "8px 0",
          borderBottom: `2px solid${appThemeDesc.palette.common.titleUnderlineColor}`
        }
      }
    },

    MuiAlert: {
      message: {
        width: "100%"
      }
    }
  }
}

export const defaultTableTheme = {
  overrides: {
    MUIDataTable: {
      root: {},
      paper: {
        borderRadius: appThemeDesc.shape.borderRadius,
        maxWidth: "100%",
        boxSizing: "border-box"
      }
    },
    MUIDataTableToolbarSelect: {
      root: {
        paddingLeft: "24px",
        paddingRight: "24px"
      },
      title: {
        paddingLeft: 0
      }
    },
    // table header
    MUIDataTableHeadRow: {
      root: {
        backgroundColor: appThemeDesc.palette.primary.main,
        color: appThemeDesc.palette.primary.contrastText,
        textAlign: "left",
        "& th": {
          backgroundColor: appThemeDesc.palette.primary.main,
          padding: "6px 24px 6px 16px",
          "& p": {
            fontSize: "0.875rem",
            padding: 0,
            fontWeight: "bold"
          },
          "& div": {
            fontSize: "12px",
            fontWeight: "bold",
            // padding: '4px 8px',
            color: appThemeDesc.palette.primary.contrastText
          }
        }
      }
    },
    // each cell
    MUIDataTableHeadCell: {
      // sortActive: {
      //   fontWeight: 'bold',
      //   color: appThemeDesc.palette.secondary.main,
      // },
      sortAction: {
        '& svg[class*="MuiTableSortLabel"]': {
          color: `${appThemeDesc.palette.secondary.main}!important`,
          fontSize: "14px"
        }
      }
    },
    // body cells
    MUIDataTableBodyRow: {
      root: {
        "& td": {
          fontSize: "0.75rem",
          padding: "4px 8px",
          borderRight: `1px solid${appThemeDesc.palette.secondary.main}`,
          borderLeft: `1px solid${appThemeDesc.palette.secondary.main}`
        }
      }
    },
    // Checkboxes cells
    MuiCheckbox: {
      root: {
        color: appThemeDesc.palette.secondary.main,
        "&.Mui-checked": {
          color: `${appThemeDesc.palette.secondary.main}!important`
        }
      }
    },
    // Text for empty table body
    MUIDataTableBody: {
      emptyTitle: {
        fontSize: "12px",
        fontStyle: "italic",
        color: appThemeDesc.palette.text.secondary
      }
    },
    // Main button icon
    MUIDataTableToolbar: {
      actions: {
        '& button[title^="Create"]': {
          backgroundColor: appThemeDesc.palette.secondary.dark,
          color: appThemeDesc.palette.common.white
        },
        '& button[title^="Create"]:hover': {
          color: appThemeDesc.palette.primary.light
        },
        '& button[title^="Ajouter"]': {
          backgroundColor: appThemeDesc.palette.secondary.dark,
          color: appThemeDesc.palette.common.white
        },
        '& button[title^="Ajouter"]:hover': {
          color: appThemeDesc.palette.primary.light
        }
      }
    },
    // Filters
    MUIDataTableFilterList: {
      root: {
        backgroundColor: "inherit",
        margin: "0",
        padding: "4px 6px",
        display: "flex",
        alignItems: "center",
        borderTop: `1px solid ${appThemeDesc.palette.common.lightGray}`,

        "&:empty": {
          padding: "0"
        }
      },
      chip: {
        margin: "4px",
        height: "auto",
        padding: "2px 0",
        background: appThemeDesc.palette.common.lightGray,
        color: appThemeDesc.palette.primary.light,

        "&:focus": {
          color: appThemeDesc.palette.primary.main,
          background: appThemeDesc.palette.common.lightGray,

          "& svg": {
            fill: appThemeDesc.palette.primary.main
          }
        },

        "& svg": {
          fill: appThemeDesc.palette.primary.light
        }
      }
    }
  }
}

const appTheme = createTheme(_.merge(defaultAppTheme, appThemeDef))
const tableTheme = createTheme(_.merge(defaultTableTheme, tableThemeDef))
const widgetTableTheme = widgetTableThemeDef
  ? createTheme(widgetTableThemeDef)
  : undefined
export { appTheme, tableTheme, widgetTableTheme }
